import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import JigsawPuzzle from '../../utils/jigsawPuzzle';
import { initStageReport, fetchGameStages, updateStageProgress } from '../../functions/stageReport';
import { fetchReport } from '../../functions/fetchReport';
import { sendReport } from '../../functions/sendReport';
import LivePuzzleBoard from '../../components/LivePuzzleBoard/LivePuzzleBoard';
import { computePuzzleGrid } from '../../utils/puzzleGrid';
import { getPuzzleThemeConfig } from '../../utils/puzzleTheme';
import {
  areAssignmentRoundsComplete,
  hasStageAnswerInReport,
  readStoredUserData,
} from '../../utils/gameStages';
import { getStagePath } from '../../utils/stageRoutes';
import {
  buildFirebaseSessionId,
  getPlacedTileIndices,
  patchUserPuzzleState,
  registerUserPuzzleSession,
  sanitizeFirebaseKey,
} from '../../utils/puzzleFirebase';
import { formatGameTimer } from '../../utils/gameTimer';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import './puzzle.css';

const PUZZLE_TIMER_KEY = 'bigpicture_puzzle_timer';
const PUZZLE_PLAY_SECONDS = 60;
const JIGSAW_TILE_ROWS = 3;
const JIGSAW_TILE_COLS = 3;
const PARTICIPATION_POINTS = 1;

function getStoredTimerStart(userId, sessionId) {
  try {
    const key = `${PUZZLE_TIMER_KEY}_${userId}_${sessionId}`;
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.startTime) return parsed.startTime;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function setStoredTimerStart(userId, sessionId, startTime) {
  try {
    const key = `${PUZZLE_TIMER_KEY}_${userId}_${sessionId}`;
    sessionStorage.setItem(key, JSON.stringify({ startTime }));
  } catch {
    /* ignore */
  }
}

function clearStoredTimer(userId, sessionId) {
  try {
    const key = `${PUZZLE_TIMER_KEY}_${userId}_${sessionId}`;
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function resolvePuzzleImageSrc(themeData, puzzleId) {
  const config = getPuzzleThemeConfig(themeData, puzzleId);
  const slices = config.slices ?? [];
  const id = Number(puzzleId ?? config.puzzleId);
  if (Number.isFinite(id) && id >= 1 && slices[id - 1]) {
    return slices[id - 1];
  }

  const totalPuzzles = Math.floor(Number(themeData?.total_puzzles ?? themeData?.puzzle_parts)) || 9;
  const rawIdx = Number(puzzleId) || Math.floor(Math.random() * totalPuzzles) + 1;
  const idx = Math.max(1, Math.min(totalPuzzles, rawIdx));
  const localFallback = `${process.env.PUBLIC_URL || ''}/img/puzzle${idx}.jpg`.replace('//img', '/img');

  return config.image || localFallback;
}

function ClockIcon({ className = 'puzzle-timer-icon' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <span className="puzzle-icon" aria-hidden>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="m12 17.27 6.18 3.73-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21Z"
        />
      </svg>
    </span>
  );
}

export default function Puzzle() {
  const navigate = useNavigate();
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);

  const boardRef = useRef(null);
  const previewRef = useRef(null);
  const puzzleInstanceRef = useRef(null);
  const completingRef = useRef(false);
  const timerStartRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const initCalledRef = useRef(false);
  const handleFinishedRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleTimerExpiredRef = useRef(null);

  const [secondsLeft, setSecondsLeft] = useState(PUZZLE_PLAY_SECONDS);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [points, setPoints] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [message, setMessage] = useState('');
  const [puzzleImage, setPuzzleImage] = useState('');
  const [boardImage, setBoardImage] = useState('');
  const [puzzleId, setPuzzleId] = useState(null);
  const [sliceUrls, setSliceUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const firebaseSyncRef = useRef({ sessionId: null, userId: null, puzzleId: null });
  const syncThrottleRef = useRef(null);
  const puzzleDragActiveRef = useRef(false);
  const scrollLockYRef = useRef(0);

  const title =
    String(themeData?.landing_page_title4 ?? themeData?.landing_title ?? 'Big Picture').trim() ||
    'Big Picture';
  const titleColor = themeData?.landing_page_title_color ?? themeData?.text_color ?? null;
  const buttonBg = themeData?.landing_page_button_bgcolor ?? themeData?.box_bg_color ?? null;
  const accentColor = themeData?.button_color ?? themeData?.box_bg_color ?? null;
  const box_text_color = themeData?.box_text_color ?? themeData?.box_text_color ?? null;

  const { macroRows, macroCols } = useMemo(() => {
    const count =
      sliceUrls.length ||
      Math.floor(Number(themeData?.total_puzzles ?? themeData?.puzzle_parts)) ||
      9;
    const savedRows = Math.floor(Number(themeData?.row));
    const savedCols = Math.floor(Number(themeData?.col));
    if (
      Number.isFinite(savedRows) &&
      Number.isFinite(savedCols) &&
      savedRows * savedCols === count
    ) {
      return { macroRows: savedRows, macroCols: savedCols };
    }
    const grid = computePuzzleGrid(count);
    return { macroRows: grid.rows, macroCols: grid.cols };
  }, [sliceUrls.length, themeData?.row, themeData?.col, themeData?.total_puzzles, themeData?.puzzle_parts]);

  const numRows = JIGSAW_TILE_ROWS;
  const numCols = JIGSAW_TILE_COLS;

  const pageStyle = useMemo(
    () => ({
      '--puzzle-title-color': titleColor || undefined,
      '--puzzle-pill-border': buttonBg || undefined,
      '--puzzle-icon-color': accentColor || buttonBg || undefined,
      '--puzzle-box-color': box_text_color || buttonBg || undefined,
      '--puzzle-accent': accentColor || buttonBg || undefined,
      '--puzzle-box-text': box_text_color || undefined,
    }),
    [titleColor, buttonBg, accentColor, box_text_color]
  );

  const getRemainingSeconds = useCallback(() => {
    if (!timerStartRef.current) return PUZZLE_PLAY_SECONDS;
    const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
    return Math.max(0, PUZZLE_PLAY_SECONDS - elapsed);
  }, []);

  const getElapsedSeconds = useCallback(() => {
    if (!timerStartRef.current) return 0;
    const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
    return Math.min(PUZZLE_PLAY_SECONDS, Math.max(0, elapsed));
  }, []);

  const countdownDisplay = timerReady
    ? formatGameTimer(secondsLeft)
    : formatGameTimer(PUZZLE_PLAY_SECONDS);

  const syncFirebasePuzzleState = useCallback(async (patch = {}) => {
    const { sessionId, userId } = firebaseSyncRef.current;
    if (!sessionId || !userId) return;
    const instance = puzzleInstanceRef.current;
    const totalTiles = numRows * numCols;
    const placedTiles =
      patch.completed === true
        ? Array.from({ length: totalTiles }, (_, i) => i)
        : instance
          ? getPlacedTileIndices(instance)
          : patch.placedTiles || [];
    const payload = {
      name: user?.name || user?.email || 'Player',
      puzzle_id: firebaseSyncRef.current.puzzleId,
      totalTiles,
      microRows: numRows,
      microCols: numCols,
      placedTiles,
      ...patch,
    };
    if (patch.completed !== undefined) {
      payload.completed = Boolean(patch.completed);
    }
    try {
      await patchUserPuzzleState(sessionId, userId, payload);
    } catch (err) {
      console.warn('Failed to sync puzzle state:', err);
    }
  }, [numCols, numRows, user?.email, user?.name]);

  const queueFirebaseSync = useCallback(
    (patch = {}) => {
      if (syncThrottleRef.current) {
        window.clearTimeout(syncThrottleRef.current);
      }
      syncThrottleRef.current = window.setTimeout(() => {
        syncFirebasePuzzleState(patch);
      }, 120);
    },
    [syncFirebasePuzzleState]
  );

  const lockPuzzlePageScroll = useCallback(() => {
    if (puzzleDragActiveRef.current) return;
    puzzleDragActiveRef.current = true;
    scrollLockYRef.current = window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add('puzzle-scroll-lock');
    document.body.classList.add('puzzle-scroll-lock');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockYRef.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }, []);

  const unlockPuzzlePageScroll = useCallback(() => {
    if (!puzzleDragActiveRef.current) return;
    puzzleDragActiveRef.current = false;
    const scrollY = scrollLockYRef.current;
    document.documentElement.classList.remove('puzzle-scroll-lock');
    document.body.classList.remove('puzzle-scroll-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }, []);

  useEffect(() => {
    const blockTouchScrollWhileDragging = (evt) => {
      if (!puzzleDragActiveRef.current) return;
      if (evt.cancelable) {
        evt.preventDefault();
      }
    };

    document.addEventListener('touchmove', blockTouchScrollWhileDragging, { passive: false });

    return () => {
      document.removeEventListener('touchmove', blockTouchScrollWhileDragging);
    };
  }, []);

  useEffect(() => {
    if (initCalledRef.current) return;
    if (!themeData) return;

    initCalledRef.current = true;

    const fetchPuzzleData = async () => {
      const userId = user?.userId;
      const organizationId = user?.organizationId;
      const sessionId = user?.sessionId;
      const token =
        adminToken ||
        (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

      if (!token || !userId || !organizationId || !sessionId) {
        setMessage('Unable to load puzzle. Please sign in again.');
        setLoading(false);
        return;
      }

      try {
        const [stageData, reportResp] = await Promise.all([
          fetchGameStages({ token, userId, organizationId, sessionId }),
          fetchReport({ userId, token, sessionId, organizationId }),
        ]);
        const nextDay = stageData?.nextDay;
        const game = stageData?.game ?? null;
        const report = reportResp?.report ?? null;

        const reportStatus = String(report?.status ?? '').trim().toLowerCase();
        if (reportStatus === 'completed') {
          navigate('/big-picture', { replace: true });
          return;
        }

        // Keep users on puzzle after completing the 3 assigned stages.
        // Only send to big-picture after overall stage flow has advanced beyond stage 8.
        if (nextDay != null && nextDay > 8) {
          navigate('/big-picture', { replace: true });
          return;
        }

        if (
          !areAssignmentRoundsComplete(report, game) &&
          nextDay >= 1 &&
          nextDay <= 5 &&
          game
        ) {
          const stageNum = Number(game[`s${nextDay}`]);
          if (
            Number.isFinite(stageNum) &&
            stageNum >= 1 &&
            !hasStageAnswerInReport(report, stageNum)
          ) {
            navigate(getStagePath(stageNum) || '/puzzle', { replace: true });
            return;
          }
        }

        const stored = readStoredUserData();
        let puzzleId =
          game?.puzzle_id ??
          stored?.puzzle_id ??
          stored?.puzzleid ??
          null;

        const data = await initStageReport({
          token,
          userId,
          email: user?.email,
          name: user?.name,
          userType: user?.role || user?.source || 'ORG_USER',
          reportId: user?.reportId || '',
          themeName: 'Big Picture',
          totalPuzzles: themeData?.total_puzzles ?? themeData?.puzzle_parts ?? 9,
        });

        puzzleId =
          puzzleId ??
          data?.game?.stages?.puzzle_id ??
          data?.game?.puzzle_id ??
          null;
        const themeConfig = getPuzzleThemeConfig(themeData, puzzleId);
        const slices = themeConfig.slices ?? [];
        const imageSrc = resolvePuzzleImageSrc(themeData, puzzleId);
        setPuzzleId(puzzleId);
        setSliceUrls(slices);
        setPuzzleImage(imageSrc);
        setBoardImage('');

        const firebaseSessionId = buildFirebaseSessionId(organizationId, sessionId);
        const firebaseUserId = sanitizeFirebaseKey(userId);
        firebaseSyncRef.current = {
          sessionId: firebaseSessionId,
          userId: firebaseUserId,
          puzzleId: puzzleId != null ? Number(puzzleId) : null,
        };
        if (puzzleId != null) {
          registerUserPuzzleSession(firebaseSessionId, firebaseUserId, {
            name: user?.name || user?.email || 'Player',
            puzzle_id: Number(puzzleId),
            totalTiles: numRows * numCols,
            microRows: numRows,
            microCols: numCols,
          }).catch((err) => console.warn('Failed to register puzzle session:', err));
        }

        const existingScore = Number(stored?.score ?? stored?.points ?? 0);
        if (Number.isFinite(existingScore) && existingScore > 0) {
          setPoints(existingScore);
        }

        const storedStart = getStoredTimerStart(userId, sessionId);
        if (storedStart) {
          timerStartRef.current = storedStart;
        } else {
          timerStartRef.current = Date.now();
          setStoredTimerStart(userId, sessionId, timerStartRef.current);
        }

        const remaining = Math.max(
          0,
          PUZZLE_PLAY_SECONDS -
            Math.floor((Date.now() - timerStartRef.current) / 1000)
        );
        setSecondsLeft(remaining);
        setTimerExpired(remaining <= 0);
        setTimerReady(true);
      } catch (err) {
        setMessage(err?.message || 'Failed to load puzzle configuration.');
      } finally {
        setLoading(false);
      }
    };

    fetchPuzzleData();
  }, [adminToken, user, themeData, navigate, numRows, numCols]);

  useEffect(() => {
    if (loading || !timerReady || !timerStartRef.current || timerExpired) {
      return undefined;
    }

    const tick = () => {
      const remaining = getRemainingSeconds();
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setTimerExpired(true);
        if (timerIntervalRef.current) {
          window.clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }
    };

    tick();
    timerIntervalRef.current = window.setInterval(tick, 1000);

    return () => {
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [loading, timerReady, timerExpired, getRemainingSeconds]);

  const saveCompletion = useCallback(async () => {
    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your progress. Please sign in again.');
      return false;
    }

    const finalPoints = PARTICIPATION_POINTS;
    const elapsedSeconds = getElapsedSeconds();
    const puzzleAnswer = JSON.stringify({
      completed: true,
      puzzle_id: puzzleId != null ? Number(puzzleId) : null,
    });

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles:
          themeData?.total_puzzles ?? themeData?.puzzle_parts ?? sliceUrls.length ?? 9,
      });

      const stageData = await fetchGameStages({
        token,
        userId,
        organizationId,
        sessionId,
      });
      const reportTimes = buildCumulativeStageTimes(stageData, elapsedSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        status: 'completed',
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: puzzleAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });

      clearStoredTimer(userId, sessionId);
      setPoints((prev) => prev + finalPoints);
      await syncFirebasePuzzleState({ completed: true });
      return true;
    } catch (err) {
      setMessage(err?.message || 'Failed to save puzzle progress.');
      return false;
    }
  }, [adminToken, puzzleId, user, getElapsedSeconds, syncFirebasePuzzleState, themeData, sliceUrls.length]);

  const handleTimerExpired = useCallback(() => {
    if (completingRef.current || autoSubmitRef.current) return;
    autoSubmitRef.current = true;
    completingRef.current = true;

    if (timerIntervalRef.current) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setSecondsLeft(0);
    setTimerExpired(true);
    setShowCelebration(true);

    saveCompletion().then((ok) => {
      window.setTimeout(() => {
        if (ok) {
          navigate('/big-picture', { replace: true });
        } else {
          completingRef.current = false;
          autoSubmitRef.current = false;
          setShowCelebration(false);
        }
      }, 3000);
    });
  }, [navigate, saveCompletion]);

  handleTimerExpiredRef.current = handleTimerExpired;

  useEffect(() => {
    if (loading || !timerReady || !timerExpired) return;
    handleTimerExpiredRef.current?.();
  }, [loading, timerReady, timerExpired]);

  const handleFinished = useCallback(
    (evt) => {
      if (completingRef.current) return;
      completingRef.current = true;

      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      window.setTimeout(() => {
        if (evt?.self?.fullImg) {
          Object.assign(evt.self.fullImg.style, {
            opacity: 1,
            zIndex: 1,
          });
        }
      }, 300);

      window.setTimeout(async () => {
        setShowCelebration(true);
        const ok = await saveCompletion();
        window.setTimeout(() => {
          if (ok) {
            navigate('/big-picture', { replace: true });
          } else {
            completingRef.current = false;
            setShowCelebration(false);
          }
        }, 3000);
      }, 1500);
    },
    [navigate, saveCompletion]
  );

  handleFinishedRef.current = handleFinished;

  const handlePreviewLoad = useCallback((evt) => {
    const loadedSrc = evt.currentTarget.currentSrc || evt.currentTarget.src;
    if (loadedSrc) {
      setBoardImage(loadedSrc);
    }
  }, []);

  const handlePreviewError = useCallback(() => {
    const fallback = `${process.env.PUBLIC_URL || ''}/img/puzzle1.jpg`.replace('//img', '/img');
    setPuzzleImage(fallback);
    setBoardImage(fallback);
  }, []);

  useEffect(() => {
    if (!puzzleImage || loading) return;
    const img = previewRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      const loadedSrc = img.currentSrc || img.src;
      if (loadedSrc) setBoardImage(loadedSrc);
    }
  }, [puzzleImage, loading]);

  useEffect(() => {
    const el = boardRef.current;
    if (!el || !boardImage || loading) return undefined;

    const onDragEnter = (evt) => {
      if (!evt.target.draggable) evt.target.classList.add('highlight');
    };
    const onDragLeave = (evt) => {
      if (!evt.target.draggable) evt.target.classList.remove('highlight');
    };
    const onMouseDown = (evt) => {
      evt.target.children[0]?.classList.add('highlight');
    };
    const onMouseUp = (evt) => {
      evt.target.children[0]?.classList.remove('highlight');
    };

    el.innerHTML = '';

    const puzzle = new JigsawPuzzle({
      el,
      image: boardImage,
      numcolumns: numCols,
      numrows: numRows,
      difficulty: 'expert',
      dragenter: onDragEnter,
      dragleave: onDragLeave,
      mousedown: onMouseDown,
      mouseup: onMouseUp,
      correct: () => {
        queueFirebaseSync();
      },
      drop: () => {
        queueFirebaseSync();
      },
      finished: (evt) => handleFinishedRef.current?.(evt),
      touchstart: lockPuzzlePageScroll,
      touchend: unlockPuzzlePageScroll,
    });

    puzzleInstanceRef.current = puzzle.init();
    el.pz = puzzleInstanceRef.current;

    const layoutTimer = window.setTimeout(() => {
      puzzleInstanceRef.current?.resize?.();
    }, 60);

    const syncTimer = window.setTimeout(() => {
      queueFirebaseSync();
    }, 900);

    let resizeRaf = null;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (resizeRaf != null) {
              window.cancelAnimationFrame(resizeRaf);
            }
            resizeRaf = window.requestAnimationFrame(() => {
              resizeRaf = null;
              puzzleInstanceRef.current?.resize?.();
            });
          })
        : null;
    resizeObserver?.observe(el);

    return () => {
      window.clearTimeout(layoutTimer);
      window.clearTimeout(syncTimer);
      if (resizeRaf != null) {
        window.cancelAnimationFrame(resizeRaf);
      }
      resizeObserver?.disconnect();
      if (syncThrottleRef.current) {
        window.clearTimeout(syncThrottleRef.current);
      }
      unlockPuzzlePageScroll();
      el.innerHTML = '';
      el.pz = null;
      puzzleInstanceRef.current = null;
    };
  }, [boardImage, numCols, numRows, loading, queueFirebaseSync, lockPuzzlePageScroll, unlockPuzzlePageScroll]);

  useEffect(() => {
    return () => {
      puzzleDragActiveRef.current = false;
      document.documentElement.classList.remove('puzzle-scroll-lock');
      document.body.classList.remove('puzzle-scroll-lock');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
    };
  }, []);

  if (loading) {
    return (
      <div className="puzzle-page" style={pageStyle}>
        <p className="puzzle-empty">Loading puzzle…</p>
      </div>
    );
  }

  if (!puzzleImage) {
    return (
      <div className="puzzle-page" style={pageStyle}>
        <p className="puzzle-empty">
          {message || 'Puzzle is not configured. Please try again.'}
        </p>
      </div>
    );
  }

  return (
    <div className="puzzle-page" style={pageStyle}>
      <header className="puzzle-top-bar">
        <div className="puzzle-top-timer" aria-label="Stage timer" aria-live="polite">
          <ClockIcon />
          <span className="puzzle-timer-value">{countdownDisplay}</span>
        </div>
      </header>

      <div className="puzzle-heading-wrap">
        <h1 className="puzzle-heading">{title}</h1>
      </div>

      <div className="puzzle-play-area">
        <div className="puzzle-preview-col">
          <aside className="puzzle-preview-card">
            <h2 className="puzzle-preview-title">Your piece</h2>
            {puzzleId != null ? (
              <p className="puzzle-preview-piece-id">Part {puzzleId}</p>
            ) : null}
            <img
              ref={previewRef}
              src={puzzleImage}
              alt="Your puzzle piece reference"
              className="puzzle-preview-image"
              onLoad={handlePreviewLoad}
              onError={handlePreviewError}
            />
          </aside>
        </div>
        <div className="puzzle-board-col">
          <div className="puzzle-board-shell">
            <h2 className="puzzle-board-label">Solve the jigsaw</h2>
            <p className="puzzle-board-label-hint">
              (Drag and drop the puzzle pieces into the correct positions to complete your
              section of the Big Picture.)
            </p>
            <div
              id="puzzle1"
              ref={boardRef}
              className={`puzzle-board${timerExpired ? ' puzzle-board--locked' : ''}`}
            >
              {!boardImage ? (
                <p className="puzzle-board-loading">Loading puzzle pieces…</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="puzzle-live-col">
          <LivePuzzleBoard
            sessionId={user?.sessionId}
            organizationId={user?.organizationId}
            sliceUrls={sliceUrls}
            macroRows={macroRows}
            macroCols={macroCols}
            tileRows={numRows}
            tileCols={numCols}
            currentUserId={user?.userId ? String(user.userId) : null}
          />
        </div>
      </div>

      {message ? <p className="puzzle-message">{message}</p> : null}

      {showCelebration ? (
        <div className="puzzle-celebration-overlay" role="dialog" aria-modal="true">
          <div className="puzzle-celebration-card">
            <img
              src="/thankyou.gif"
              alt="Celebration"
              className="puzzle-celebration-gif"
            />
            <h2>Awesome!</h2>
            <p>Puzzle complete — saving your progress…</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
